<template>
    <application-layout>
        <v-main>
            <v-container>
                <v-form ref="editCityPageForm" @submit.prevent="submitCityPageForm">
                    <h1>Manage City Pages</h1>

                    <v-row>
                        <v-col md="6">
                            <v-select
                                data-testid="city-page-state-select"
                                class="city-page-state-select"
                                filled
                                label="Select State"
                                :items="states"
                                @change="handleStateChange"
                                :value="selectedState"
                                clearable
                            />
                        </v-col>
                        <v-col md="6" v-if="selectedState && cities">
                            <v-select
                                data-testid="city-page-city-select"
                                class="city-page-city-select"
                                filled
                                label="Select City"
                                :items="cities"
                                @change="handleCityChange"
                                :value="selectedCity"
                                clearable
                            />
                        </v-col>
                    </v-row>

                    <div v-if="currentCityPage">
                        <v-row>
                            <v-col>
                                <h2>SEO</h2>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col md="4">
                                <app-input
                                    data-testid="city-page-title-input"
                                    class="city-page-title-input"
                                    :type="'text'"
                                    label="Page Title"
                                    v-model.trim="currentCityPage.page_title.content"
                                />
                            </v-col>
                            <v-col md="4">
                                <app-input
                                    data-testid="city-page-description-input"
                                    class="city-page-description-input"
                                    :type="'text'"
                                    label="Meta Description"
                                    v-model.trim="currentCityPage.meta_description.content"
                                />
                            </v-col>
                            <v-col md="4">
                                <app-input
                                    data-testid="city-page-keywords-input"
                                    class="city-page-keywords-input"
                                    :type="'text'"
                                    label="Meta Keywords"
                                    v-model.trim="currentCityPage.meta_keywords.content"
                                />
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col>
                                <h2>Page Content</h2>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col
                                class="has-image"
                                v-if="isValidImage(currentCityPage)"
                            >
                                <h3 class="mb-2">Under Map Image</h3>
                                <v-img
                                    :src="`${imageBasePath}${JSON.parse(currentCityPage.promo_image.content).url}`"
                                    aspect-ratio="1"
                                >
                                    <v-row
                                        class="fill-height ma-0"
                                        slot="placeholder"
                                        align="center"
                                        justify="center"
                                    >
                                        <v-progress-circular indeterminate color="grey" />
                                    </v-row>
                                </v-img>
                                <v-btn
                                    data-testid="city-page-delete-btn"
                                    class="city-page-delete-btn my-3"
                                    color="error"
                                    small
                                    block
                                    @click="openDelete(currentCityPage.promo_image)"
                                >
                                    Delete Image
                                </v-btn>
                              <app-input
                                data-testid="city-page-promo-img-alt-text-input"
                                class="city-page-promo-img-alt-text-input"
                                v-model="promoImgAltText"
                                :type="'text'"
                                :label="'Promo Image Alt Text'"
                                clearable
                              />
                            </v-col>
                            <v-col v-else class="no-image">
                                <h3>Under Map Image</h3>
                                <add-photo
                                    data-testid="city-page-add-btn"
                                    class="city-page-add-btn"
                                    dispatchMethod="uploadCityPromoImage"
                                    :additionalData="{ident: currentCityPage.promo_image.ident}"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <h3 class="mb-2">Under Map Section</h3>
                                <froala
                                    data-testid="city-page-map-froala-textarea"
                                    class="city-page-map-froala-textarea"
                                    :tag="'textarea'"
                                    :config="froalaConfig"
                                    v-model.trim="currentCityPage.under_map_section.content"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <h3 class="mb-2">Bottom Content Section</h3>
                                <froala
                                    data-testid="city-page-content-froala-textarea"
                                    class="city-page-content-froala-textarea"
                                    :tag="'textarea'"
                                    :config="froalaConfig"
                                    v-model.trim="currentCityPage.bottom_content_section.content"
                                />
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col>
                                <h2>Custom Code</h2>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col md="4">
                                <v-textarea
                                    data-testid="city-page-markup-head-textarea"
                                    class="city-page-markup-head-textarea"
                                    filled
                                    label="Custom Markup (head)"
                                    v-model.trim="currentCityPage.custom_markup_head.content"
                                    clearable
                                />
                            </v-col>
                            <v-col md="4">
                                <v-textarea
                                    data-testid="city-page-markup-body-textarea"
                                    class="city-page-markup-body-textarea"
                                    filled
                                    label="Custom Markup (body)"
                                    v-model.trim="currentCityPage.custom_markup_body.content"
                                    clearable
                                />
                            </v-col>
                            <v-col md="4">
                                <v-textarea
                                    data-testid="city-page-css-textarea"
                                    class="city-page-css-textarea"
                                    filled
                                    label="Custom CSS"
                                    v-model.trim="currentCityPage.custom_css.content"
                                    clearable
                                />
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col>
                                <v-btn
                                    data-testid="city-page-save-btn"
                                    class="city-page-save-btn ml-0"
                                    color="primary"
                                    type="submit"
                                    :loading="loading"
                                    :disabled="loading"
                                >
                                  Save
                                </v-btn>
                            </v-col>
                        </v-row>
                    </div>
                </v-form>
            </v-container>
        </v-main>

        <delete-modal
            v-model="modal"
            @delete="deletePhoto"
            :itemType="'photo'"
            :advisory="advisory"
        />
    </application-layout>
</template>

<script>
import AddPhoto from "@/components/AddPhoto.vue";
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { genericAdvisory } from "@/helpers/warning-advisory-text";
import { isValidImage } from "@/helpers/location/valid-img-function";
import { mapState } from "vuex";

export default {
  data() {
    return {
      advisory: genericAdvisory,
      imageBasePath: `${process.env.VUE_APP_PHOTO_BASE_PATH}/`,
      loading: false,
      modal: false,
      selectedSection: ""
    };
  },

  components: {
    AddPhoto,
    ApplicationLayout,
    DeleteModal
  },

  computed: {
    ...mapState({
      promoImgTxt: state => state.location.promoImgText
    }),
    currentCityPage: {
      get() {
        return this.$store.state.location.currentCityPage;
      },
      set(newValue) {
        this.$store.commit("setCurrentCityPageData", newValue);
      }
    },
    selectedState() {
      return this.$store.state.location.selectedState;
    },
    selectedCity() {
      return this.$store.state.location.selectedCity;
    },
    states() {
      return this.$store.state.location.states;
    },
    cities() {
      return this.$store.state.location.cities;
    },
    froalaConfig() {
      return {
        ...this.$store.state.app.froalaConfig,
        theme: this.$vuetify.theme.dark ? "dark" : null,
        requestHeaders: {
          Authorization: this.$store.state.auth.access_token
        }
      };
    },
    promoImgAltText: {
      get() {
        const promoImgContent = JSON.parse(this.currentCityPage.promo_image.content);
        return this.currentCityPage && promoImgContent && promoImgContent.alt_text
          ? promoImgContent.alt_text : this.promoImgTxt;
      },
      set(newValue) {
        this.$store.dispatch("handlePromoImgAltText", newValue);
      }
    }
  },

  methods: {
    isValidImage: isValidImage,
    handleStateChange(selectedValue) {
      this.$store.commit("setCurrentState", selectedValue);
      this.$store.dispatch("getAllCitiesForState");
    },

    handleCityChange(selectedValue) {
      this.$store.dispatch("selectCurrentCity", selectedValue);
    },

    submitCityPageForm() {
      if (this.$refs.editCityPageForm.validate()) {
        this.loading = true;
        this.$store.dispatch("updateCityPage")
          .finally(() => {
            this.loading = false;
          });
      }
    },

    openDelete(section) {
      this.modal = true;
      this.selectedSection = section;
    },

    deletePhoto() {
      this.loading = true;
      this.$store.dispatch("deleteStateCityImage", {
        imageId: JSON.parse(this.selectedSection.content).id,
        ident: this.selectedSection.ident
      }).then(() => {
        this.loading = false;
        this.$store.dispatch("getCityPageContent", this.selectedState);
        this.modal = false;
        this.selectedSection = "";
      });
    }
  },

  created() {
    this.$store.commit("setCurrentState", null);
    this.$store.commit("setCurrentCity", null);
    this.$store.commit("setCurrentCityPageData", null);
    this.$store.dispatch("getAllStates");
  }
};
</script>

<style lang="scss" scoped>
    .has-image {
        max-width: 300px;
    }
    .no-image {
        max-width: 300px;
        min-height: 200px;
    }
</style>
